import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner, Accordion } from 'react-bootstrap'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_ALL_PIPELINES_FULL } from '../../gql/queries/pipelines'
import { GET_ALL_LEAD_SOURCE_CATEGORIES } from '../../gql/queries/leadSourceQuery'
import { GENERATE_PUBLIC_TOKEN } from '../../gql/mutations/publicTokenGenerator'
import { GET_ALL_USERS_LIST } from '../../gql/queries/usersQuery'
import { GET_ALL_SIGNATURE_FORMS_ACTIVE } from '../../gql/queries/signatureFormsApiQueries'
import { parseId } from '../../helpers'
import { ADD_EDIT_LEAD_SOURCES } from '../../gql/mutations/leadSourceMutations'
import { IResAllListUsers } from '../../types/users'
import { IResSignatureFormsAPI } from '../../types/signatureFormsApi'

interface Props {
    show: boolean
    onHide: () => void
}

// Formato esperado por gtag/GTM: "AW-XXXXXXX/CONVERSION_LABEL"
const AW_CONVERSION_REGEX = /^AW-\d+\/[A-Za-z0-9_-]+$/
const isValidConversionId = (value: string) => !value.trim() || AW_CONVERSION_REGEX.test(value.trim())

export const GenerateTokenModal: React.FC<Props> = ({ show, onHide }) => {
    const [stagePipelineId, setStagePipelineId] = useState<string>('')
    const [pipelineId, setPipelineId] = useState<string>('')
    const [leadSourceCategoryId, setLeadSourceCategoryId] = useState<string>('')
    const [leadSourceId, setLeadSourceId] = useState<string>('')
    const [campusId, setCampusId] = useState<string>('1')
    const [stagePipelineIntentId, setStagePipelineIntentId] = useState<string>('')
    const [stagePipelineSignedId, setStagePipelineSignedId] = useState<string>('')
    const [stagePipelineQuotedId, setStagePipelineQuotedId] = useState<string>('')
    const [agentId, setAgentId] = useState<string>('')
    const [signatureFormEsId, setSignatureFormEsId] = useState<string>('')
    const [signatureFormEnId, setSignatureFormEnId] = useState<string>('')
    const [sendEmail, setSendEmail] = useState<boolean>(true)
    const [sendSms, setSendSms] = useState<boolean>(false)
    const [requireIdentity, setRequireIdentity] = useState<boolean>(true)
    const [sourceDescription, setSourceDescription] = useState<string>('')
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false)
    const [generatedToken, setGeneratedToken] = useState<string>('')
    const [useShortUrl, setUseShortUrl] = useState<boolean>(false)
    const [slug, setSlug] = useState<string>('')
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [whatsappNumber, setWhatsappNumber] = useState<string>('')

    // Google GTM & Google Ads State Variables
    const [gtmBaseUrlEn, setGtmBaseUrlEn] = useState<string>('')
    const [gtmBaseUrlEs, setGtmBaseUrlEs] = useState<string>('')
    const [gtmIdEn, setGtmIdEn] = useState<string>('')
    const [gtmIdEs, setGtmIdEs] = useState<string>('')
    const [googleAdsIdEn, setGoogleAdsIdEn] = useState<string>('')
    const [googleAdsIdEs, setGoogleAdsIdEs] = useState<string>('')
    const [googleAdsPhoneEn, setGoogleAdsPhoneEn] = useState<string>('')
    const [googleAdsPhoneEs, setGoogleAdsPhoneEs] = useState<string>('')
    const [googleAdsLeadConversionIdEn, setGoogleAdsLeadConversionIdEn] = useState<string>('')
    const [googleAdsLeadConversionIdEs, setGoogleAdsLeadConversionIdEs] = useState<string>('')
    const [googleAdsConsentConversionIdEn, setGoogleAdsConsentConversionIdEn] = useState<string>('')
    const [googleAdsConsentConversionIdEs, setGoogleAdsConsentConversionIdEs] = useState<string>('')
    const [googleAdsQuoteConversionIdEn, setGoogleAdsQuoteConversionIdEn] = useState<string>('')
    const [googleAdsQuoteConversionIdEs, setGoogleAdsQuoteConversionIdEs] = useState<string>('')
    const [hasSavedConfig, setHasSavedConfig] = useState<boolean>(false)
    const [adsLanguage, setAdsLanguage] = useState<'en' | 'es'>('en')

    // Queries
    const { data: pipelinesData, loading: loadingPipelines } = useQuery(GET_ALL_PIPELINES_FULL, {
        skip: !show,
    })

    const { data: sourcesData, loading: loadingSources } = useQuery(GET_ALL_LEAD_SOURCE_CATEGORIES, {
        variables: { first: 100, offset: 0 },
        fetchPolicy: 'no-cache',
        skip: !show,
    })

    const { data: usersData, loading: loadingUsers } = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
        skip: !show,
    })

    const { data: signatureFormsData, loading: loadingSignatureForms } = useQuery<IResSignatureFormsAPI>(
        GET_ALL_SIGNATURE_FORMS_ACTIVE,
        {
            variables: { first: 100 },
            skip: !show,
        }
    )

    const [generateToken, { loading: generating }] = useMutation(GENERATE_PUBLIC_TOKEN)
    const [updateLeadSource, { loading: updatingSource }] = useMutation(ADD_EDIT_LEAD_SOURCES)

    const pipelines = pipelinesData?.allPipelinesFull?.edges?.map((e: any) => e.node) || []
    const categories = sourcesData?.allCategoryLeadSource?.edges?.map((e: any) => e.node) || []
    const users = usersData?.allUsersList || []
    const signatureForms = signatureFormsData?.allSignatureForms?.edges?.map((e: any) => e.node) || []

    // Derived stages based on selected pipeline
    const selectedPipeline = pipelines.find((p: any) => p.id === pipelineId)
    const stages = selectedPipeline?.stagePipelineSet?.edges?.map((e: any) => e.node) || []

    // Derived sources based on selected category
    const selectedCategory = categories.find((c: any) => c.id === leadSourceCategoryId)
    const filteredSources = selectedCategory?.leadSourceSet?.edges?.map((e: any) => e.node).filter((s: any) => s.enabled !== false) || []

    const selectedLeadSource = filteredSources.find((s: any) => s.id === leadSourceId)

    const hasInvalidConversionId = [
        googleAdsIdEn,
        googleAdsIdEs,
        googleAdsLeadConversionIdEn,
        googleAdsLeadConversionIdEs,
        googleAdsConsentConversionIdEn,
        googleAdsConsentConversionIdEs,
        googleAdsQuoteConversionIdEn,
        googleAdsQuoteConversionIdEs,
    ].some((value) => !isValidConversionId(value))

    useEffect(() => {
        if (selectedLeadSource) {
            setSlug(selectedLeadSource.slug || '')
            if (selectedLeadSource.metadata) {
                try {
                    const meta = typeof selectedLeadSource.metadata === 'string'
                        ? JSON.parse(selectedLeadSource.metadata)
                        : selectedLeadSource.metadata
                    setPhoneNumber(meta.phoneNumber || '')
                    setWhatsappNumber(meta.whatsappNumber || '')
                    setGtmBaseUrlEn(meta.gtmBaseUrlEn || '')
                    setGtmBaseUrlEs(meta.gtmBaseUrlEs || '')
                    setGtmIdEn(meta.gtmIdEn || '')
                    setGtmIdEs(meta.gtmIdEs || '')
                    setGoogleAdsIdEn(meta.googleAdsIdEn || '')
                    setGoogleAdsIdEs(meta.googleAdsIdEs || '')
                    setGoogleAdsPhoneEn(meta.googleAdsPhoneEn || '')
                    setGoogleAdsPhoneEs(meta.googleAdsPhoneEs || '')
                    setGoogleAdsLeadConversionIdEn(meta.googleAdsLeadConversionIdEn || '')
                    setGoogleAdsLeadConversionIdEs(meta.googleAdsLeadConversionIdEs || '')
                    setGoogleAdsConsentConversionIdEn(meta.googleAdsConsentConversionIdEn || '')
                    setGoogleAdsConsentConversionIdEs(meta.googleAdsConsentConversionIdEs || '')
                    setGoogleAdsQuoteConversionIdEn(meta.googleAdsQuoteConversionIdEn || '')
                    setGoogleAdsQuoteConversionIdEs(meta.googleAdsQuoteConversionIdEs || '')
                    setHasSavedConfig(Boolean(
                        meta.googleAdsIdEn || meta.googleAdsIdEs ||
                        meta.googleAdsLeadConversionIdEn || meta.googleAdsLeadConversionIdEs ||
                        meta.googleAdsConsentConversionIdEn || meta.googleAdsConsentConversionIdEs ||
                        meta.googleAdsQuoteConversionIdEn || meta.googleAdsQuoteConversionIdEs
                    ))
                } catch (e) {
                    console.error('Error parsing metadata', e)
                    setHasSavedConfig(false)
                }
            } else {
                setPhoneNumber('')
                setWhatsappNumber('')
                setGtmBaseUrlEn('')
                setGtmBaseUrlEs('')
                setGtmIdEn('')
                setGtmIdEs('')
                setGoogleAdsIdEn('')
                setGoogleAdsIdEs('')
                setGoogleAdsPhoneEn('')
                setGoogleAdsPhoneEs('')
                setGoogleAdsLeadConversionIdEn('')
                setGoogleAdsLeadConversionIdEs('')
                setGoogleAdsConsentConversionIdEn('')
                setGoogleAdsConsentConversionIdEs('')
                setGoogleAdsQuoteConversionIdEn('')
                setGoogleAdsQuoteConversionIdEs('')
                setHasSavedConfig(false)
            }
        }
    }, [leadSourceId, selectedLeadSource])

    useEffect(() => {
        if (!show) {
            setPipelineId('')
            setStagePipelineId('')
            setLeadSourceCategoryId('')
            setLeadSourceId('')
            setCampusId('1')
            setStagePipelineIntentId('')
            setStagePipelineSignedId('')
            setStagePipelineQuotedId('')
            setAgentId('')
            setSignatureFormEsId('')
            setSignatureFormEnId('')
            setSendEmail(true)
            setSendSms(false)
            setRequireIdentity(true)
            setSourceDescription('')
            setShowAdvanced(false)
            setGeneratedToken('')
            setUseShortUrl(false)
            setSlug('')
            setPhoneNumber('')
            setWhatsappNumber('')
            setGtmBaseUrlEn('')
            setGtmBaseUrlEs('')
            setGtmIdEn('')
            setGtmIdEs('')
            setGoogleAdsIdEn('')
            setGoogleAdsIdEs('')
            setGoogleAdsPhoneEn('')
            setGoogleAdsPhoneEs('')
            setGoogleAdsLeadConversionIdEn('')
            setGoogleAdsLeadConversionIdEs('')
            setGoogleAdsConsentConversionIdEn('')
            setGoogleAdsConsentConversionIdEs('')
            setGoogleAdsQuoteConversionIdEn('')
            setGoogleAdsQuoteConversionIdEs('')
            setHasSavedConfig(false)
            setAdsLanguage('en')
        }
    }, [show])

    const buildMetadataPayload = () => ({
        phoneNumber,
        whatsappNumber,
        gtmBaseUrlEn,
        gtmBaseUrlEs,
        gtmIdEn,
        gtmIdEs,
        googleAdsIdEn,
        googleAdsIdEs,
        googleAdsPhoneEn,
        googleAdsPhoneEs,
        googleAdsLeadConversionIdEn,
        googleAdsLeadConversionIdEs,
        googleAdsConsentConversionIdEn,
        googleAdsConsentConversionIdEs,
        googleAdsQuoteConversionIdEn,
        googleAdsQuoteConversionIdEs,
    })

    // Persiste la config de Google Ads/GTM en LeadSource.metadata (fuente única),
    // preservando name/category/slug actuales del registro.
    const persistLeadSourceMetadata = async (slugValue: string | null) => {
        if (!selectedLeadSource) return false
        try {
            const { data } = await updateLeadSource({
                variables: {
                    input: {
                        id: parseId(leadSourceId),
                        name: selectedLeadSource.name,
                        category: parseId(selectedLeadSource.category.id),
                        slug: slugValue,
                        metadata: JSON.stringify(buildMetadataPayload()),
                    },
                },
            })
            return Boolean(data?.leadSourceCreateupdateMutation?.leadSource)
        } catch (e: any) {
            toast.error(e.message || 'Error saving campaign config')
            return false
        }
    }

    const handleGenerate = async () => {
        if (!stagePipelineId || !leadSourceId || !campusId) {
            toast.error('Please fill all the required fields')
            return
        }
        if (hasInvalidConversionId) {
            toast.error('One or more Google Ads Conversion IDs have an invalid format (expected AW-XXXXXXX/LABEL)')
            return
        }

        const metadataSaved = await persistLeadSourceMetadata(selectedLeadSource?.slug || null)
        if (!metadataSaved) {
            toast.warning('Could not save Google Ads config to this Lead Source; the token will still be generated with the values entered.')
        }

        try {
            const { data } = await generateToken({
                variables: {
                    leadSourceId: Number(parseId(leadSourceId)),
                    stagePipelineId: Number(parseId(stagePipelineId)),
                    campusId: Number(campusId),
                    stagePipelineIntentId: stagePipelineIntentId ? Number(parseId(stagePipelineIntentId)) : null,
                    stagePipelineSignedId: stagePipelineSignedId ? Number(parseId(stagePipelineSignedId)) : null,
                    stagePipelineQuotedId: stagePipelineQuotedId ? Number(parseId(stagePipelineQuotedId)) : null,
                    agentId: agentId ? Number(agentId) : null,
                    signatureFormEsId: signatureFormEsId ? Number(signatureFormEsId) : null,
                    signatureFormEnId: signatureFormEnId ? Number(signatureFormEnId) : null,
                    sendEmail,
                    sendSms,
                    requireIdentity,
                    sourceDescription: sourceDescription || null,
                    googleAdsIdEn: googleAdsIdEn || null,
                    googleAdsIdEs: googleAdsIdEs || null,
                    googleAdsPhoneEn: googleAdsPhoneEn || null,
                    googleAdsPhoneEs: googleAdsPhoneEs || null,
                    googleAdsLeadConversionIdEn: googleAdsLeadConversionIdEn || null,
                    googleAdsLeadConversionIdEs: googleAdsLeadConversionIdEs || null,
                    googleAdsConsentConversionIdEn: googleAdsConsentConversionIdEn || null,
                    googleAdsConsentConversionIdEs: googleAdsConsentConversionIdEs || null,
                    googleAdsQuoteConversionIdEn: googleAdsQuoteConversionIdEn || null,
                    googleAdsQuoteConversionIdEs: googleAdsQuoteConversionIdEs || null,
                },
            })

            if (data?.generatePublicToken?.success) {
                setGeneratedToken(data.generatePublicToken.token)
                toast.success('Token generated successfully')
            } else {
                const errorMsg = data?.generatePublicToken?.errors?.[0]?.message || 'Failed to generate token'
                toast.error(errorMsg)
            }
        } catch (e: any) {
            toast.error(e.message || 'An error occurred')
        }
    }

    const handleSaveAndGenerateSlug = async () => {
        if (!slug || !leadSourceId) {
            toast.error('Slug and Lead Source are required for short URL')
            return
        }
        if (hasInvalidConversionId) {
            toast.error('One or more Google Ads Conversion IDs have an invalid format (expected AW-XXXXXXX/LABEL)')
            return
        }

        const saved = await persistLeadSourceMetadata(slug)
        if (!saved) {
            toast.error('Failed to update lead source')
            return
        }
        toast.success('Campaign configuration saved')
        // Cada idioma puede vivir en un dominio/path distinto (ej. asegurasalud vs thefutureinsurance),
        // por eso el base URL ya viene completo por idioma y solo se le agrega el slug.
        const urlEn = `${(gtmBaseUrlEn || 'https://quote.thefutureinsurance.com/en').replace(/\/$/, '')}/${slug}`
        const urlEs = `${(gtmBaseUrlEs || 'https://quote.thefutureinsurance.com/es').replace(/\/$/, '')}/${slug}`
        setGeneratedToken(`EN: ${urlEn}\nES: ${urlEs}`)
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedToken)
        toast.success('Token copied to clipboard')
    }

    return (
        <Modal show={show} onHide={onHide} centered size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Generate Lovable Campaign Token</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Accordion defaultActiveKey="0" className="mb-3">
                    {/* CATEGORY 1: Basic Configuration */}
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <span className="fw-bold text-primary">
                                <i className="bi bi-gear-wide-connected me-2"></i>1. Basic Configuration
                            </span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="mb-3">
                                <Form.Label className="fw-bold">Pipeline</Form.Label>
                                <Form.Select
                                    value={pipelineId}
                                    onChange={(e) => {
                                        setPipelineId(e.target.value)
                                        setStagePipelineId('')
                                        setGeneratedToken('')
                                    }}
                                    disabled={loadingPipelines}
                                >
                                    <option value="">Select Pipeline...</option>
                                    {pipelines.map((p: any) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="fw-bold">Stage Pipeline (Required)</Form.Label>
                                <Form.Select
                                    value={stagePipelineId}
                                    onChange={(e) => {
                                        setStagePipelineId(e.target.value)
                                        setGeneratedToken('')
                                    }}
                                    disabled={!pipelineId || stages.length === 0}
                                >
                                    <option value="">Select Stage...</option>
                                    {stages.map((s: any) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="fw-bold">Lead Source Category (Required)</Form.Label>
                                <Form.Select
                                    value={leadSourceCategoryId}
                                    onChange={(e) => {
                                        setLeadSourceCategoryId(e.target.value)
                                        setLeadSourceId('')
                                        setGeneratedToken('')
                                    }}
                                    disabled={loadingSources}
                                >
                                    <option value="">Select Category...</option>
                                    {categories.map((cat: any) => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="fw-bold">Lead Source (Required)</Form.Label>
                                <Form.Select
                                    value={leadSourceId}
                                    onChange={(e) => {
                                        setLeadSourceId(e.target.value)
                                        setGeneratedToken('')
                                    }}
                                    disabled={!leadSourceCategoryId || filteredSources.length === 0}
                                >
                                    <option value="">Select Lead Source...</option>
                                    {filteredSources.map((s: any) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-2">
                                <Form.Label className="fw-bold">Campus ID (Optional, Default is 1)</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={campusId}
                                    onChange={(e) => {
                                        setCampusId(e.target.value)
                                        setGeneratedToken('')
                                    }}
                                />
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* CATEGORY 2: Campaign & Short URL */}
                    <Accordion.Item eventKey="1">
                        <Accordion.Header>
                            <span className="fw-bold text-success">
                                <i className="bi bi-link-45deg me-2"></i>2. Campaign & Short URL
                            </span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="mb-3 border p-3 rounded bg-light">
                                <Form.Check
                                    type="switch"
                                    id="use-short-url"
                                    label="Use Short URL (Slug Based)"
                                    checked={useShortUrl}
                                    onChange={(e) => {
                                        setUseShortUrl(e.target.checked)
                                        setGeneratedToken('')
                                    }}
                                />
                                {useShortUrl && (
                                    <div className="mt-3">
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small fw-bold">URL Slug <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="agent-campaign-name"
                                                value={slug}
                                                onChange={(e) => setSlug(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small fw-bold">Custom Phone</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="+1 800-000-0000"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-0">
                                            <Form.Label className="small fw-bold">Custom WhatsApp</Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="18000000000"
                                                value={whatsappNumber}
                                                onChange={(e) => setWhatsappNumber(e.target.value)}
                                            />
                                        </Form.Group>
                                    </div>
                                )}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* CATEGORY 3: Google Tags & Google Ads */}
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>
                            <span className="fw-bold text-warning">
                                <i className="bi bi-google me-2"></i>3. Google Tags & Google Ads
                            </span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
                                <i className="bi bi-info-circle-fill me-2"></i>
                                These settings are stored on the Lead Source (single source of truth) and are embedded in every token generated for it, with or without a Short URL.
                            </div>
                            {leadSourceId && (
                                hasSavedConfig ? (
                                    <div className="alert alert-success py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        This Lead Source already has Google Ads config saved. Editing below will overwrite it on generate.
                                    </div>
                                ) : (
                                    <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        No Google Ads config saved yet for this Lead Source.
                                    </div>
                                )
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="small fw-bold d-block">Campaign Language</Form.Label>
                                <Form.Check
                                    inline
                                    type="radio"
                                    name="adsLanguage"
                                    id="ads-lang-en"
                                    label="English"
                                    checked={adsLanguage === 'en'}
                                    onChange={() => setAdsLanguage('en')}
                                />
                                <Form.Check
                                    inline
                                    type="radio"
                                    name="adsLanguage"
                                    id="ads-lang-es"
                                    label="Spanish"
                                    checked={adsLanguage === 'es'}
                                    onChange={() => setAdsLanguage('es')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Base URL</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder={adsLanguage === 'en' ? 'https://quote.thefutureinsurance.com/en' : 'https://quote.thefutureinsurance.com/es'}
                                    value={adsLanguage === 'en' ? gtmBaseUrlEn : gtmBaseUrlEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGtmBaseUrlEn(e.target.value) : setGtmBaseUrlEs(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">GTM Container ID</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder={adsLanguage === 'en' ? 'GTM-WNJZTLL8' : 'GTM-NMXMBT3'}
                                    value={adsLanguage === 'en' ? gtmIdEn : gtmIdEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGtmIdEn(e.target.value) : setGtmIdEs(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Phone Call Conversion ID/Label</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="AW-780576769/JnzJCLqx-q0cEIHQmvQC"
                                    value={adsLanguage === 'en' ? googleAdsIdEn : googleAdsIdEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGoogleAdsIdEn(e.target.value) : setGoogleAdsIdEs(e.target.value)}
                                    isInvalid={!isValidConversionId(adsLanguage === 'en' ? googleAdsIdEn : googleAdsIdEs)}
                                />
                                <Form.Control.Feedback type="invalid">Formato esperado: AW-XXXXXXX/LABEL</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Google Ads Phone</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder={adsLanguage === 'en' ? '(833) 898-2650' : '(833) 848-1539'}
                                    value={adsLanguage === 'en' ? googleAdsPhoneEn : googleAdsPhoneEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGoogleAdsPhoneEn(e.target.value) : setGoogleAdsPhoneEs(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Lead Created Conversion ID/Label</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="AW-780576769/XXXXXXXXXXX"
                                    value={adsLanguage === 'en' ? googleAdsLeadConversionIdEn : googleAdsLeadConversionIdEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGoogleAdsLeadConversionIdEn(e.target.value) : setGoogleAdsLeadConversionIdEs(e.target.value)}
                                    isInvalid={!isValidConversionId(adsLanguage === 'en' ? googleAdsLeadConversionIdEn : googleAdsLeadConversionIdEs)}
                                />
                                <Form.Control.Feedback type="invalid">Formato esperado: AW-XXXXXXX/LABEL</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Consent Signed Conversion ID/Label</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="AW-780576769/XXXXXXXXXXX"
                                    value={adsLanguage === 'en' ? googleAdsConsentConversionIdEn : googleAdsConsentConversionIdEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGoogleAdsConsentConversionIdEn(e.target.value) : setGoogleAdsConsentConversionIdEs(e.target.value)}
                                    isInvalid={!isValidConversionId(adsLanguage === 'en' ? googleAdsConsentConversionIdEn : googleAdsConsentConversionIdEs)}
                                />
                                <Form.Control.Feedback type="invalid">Formato esperado: AW-XXXXXXX/LABEL</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0">
                                <Form.Label className="small fw-bold">Quote Created Conversion ID/Label</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="AW-780576769/XXXXXXXXXXX"
                                    value={adsLanguage === 'en' ? googleAdsQuoteConversionIdEn : googleAdsQuoteConversionIdEs}
                                    onChange={(e) => adsLanguage === 'en' ? setGoogleAdsQuoteConversionIdEn(e.target.value) : setGoogleAdsQuoteConversionIdEs(e.target.value)}
                                    isInvalid={!isValidConversionId(adsLanguage === 'en' ? googleAdsQuoteConversionIdEn : googleAdsQuoteConversionIdEs)}
                                />
                                <Form.Control.Feedback type="invalid">Formato esperado: AW-XXXXXXX/LABEL</Form.Control.Feedback>
                            </Form.Group>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* CATEGORY 4: Advanced Options */}
                    <Accordion.Item eventKey="3">
                        <Accordion.Header>
                            <span className="fw-bold text-danger">
                                <i className="bi bi-sliders me-2"></i>4. Advanced Options
                            </span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Stage Pipeline Intent ID (Optional)</Form.Label>
                                <Form.Select value={stagePipelineIntentId} onChange={(e) => setStagePipelineIntentId(e.target.value)} disabled={!pipelineId || stages.length === 0}>
                                    <option value="">Select Stage...</option>
                                    {stages.map((s: any) => (
                                        <option key={`intent-${s.id}`} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Stage Pipeline Signed ID (Optional)</Form.Label>
                                <Form.Select value={stagePipelineSignedId} onChange={(e) => setStagePipelineSignedId(e.target.value)} disabled={!pipelineId || stages.length === 0}>
                                    <option value="">Select Stage...</option>
                                    {stages.map((s: any) => (
                                        <option key={`signed-${s.id}`} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Stage Pipeline Quoted ID (Optional)</Form.Label>
                                <Form.Select value={stagePipelineQuotedId} onChange={(e) => setStagePipelineQuotedId(e.target.value)} disabled={!pipelineId || stages.length === 0}>
                                    <option value="">Select Stage...</option>
                                    {stages.map((s: any) => (
                                        <option key={`quoted-${s.id}`} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Select Agent (Optional)</Form.Label>
                                <Form.Select
                                    value={agentId}
                                    onChange={(e) => setAgentId(e.target.value)}
                                    disabled={loadingUsers}
                                >
                                    <option value="">Select Agent...</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={parseId(user.id)}>
                                            {user.firstName} {user.lastName} ({user.primaryGroup?.name || 'No group'})
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Select Signature Form ES (Optional)</Form.Label>
                                <Form.Select
                                    value={signatureFormEsId}
                                    onChange={(e) => setSignatureFormEsId(e.target.value)}
                                    disabled={loadingSignatureForms}
                                >
                                    <option value="">Select Signature Form...</option>
                                    {signatureForms.map((form: any) => (
                                        <option key={`es-${form.id}`} value={parseId(form.id)}>
                                            {form.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Select Signature Form EN (Optional)</Form.Label>
                                <Form.Select
                                    value={signatureFormEnId}
                                    onChange={(e) => setSignatureFormEnId(e.target.value)}
                                    disabled={loadingSignatureForms}
                                >
                                    <option value="">Select Signature Form...</option>
                                    {signatureForms.map((form: any) => (
                                        <option key={`en-${form.id}`} value={parseId(form.id)}>
                                            {form.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="mb-3">
                                <Form.Label className="small fw-bold">Source Description (Optional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="quote.thefutureinsurance.com"
                                    value={sourceDescription}
                                    onChange={(e) => setSourceDescription(e.target.value)}
                                />
                            </div>

                            <div className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="send-email"
                                    label="Send Email"
                                    checked={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.checked)}
                                />
                                <Form.Check
                                    type="switch"
                                    id="send-sms"
                                    label="Send SMS"
                                    checked={sendSms}
                                    onChange={(e) => setSendSms(e.target.checked)}
                                />
                                <Form.Check
                                    type="switch"
                                    id="require-identity"
                                    label="Require Identity"
                                    checked={requireIdentity}
                                    onChange={(e) => setRequireIdentity(e.target.checked)}
                                />
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {generatedToken && (
                    <div className="alert alert-success d-flex flex-column align-items-center mb-0 mt-3">
                        <h6 className="mb-2 w-100 text-center fw-bold">Your Generated Token</h6>
                        <div
                            className="bg-white p-2 border rounded text-break w-100 mb-3"
                            style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-line' }}
                        >
                            {generatedToken}
                        </div>
                        <Button variant="success" size="sm" onClick={handleCopy} className="w-100 fw-bold">
                            <i className="bi bi-clipboard me-2"></i>Copy Token
                        </Button>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={useShortUrl ? handleSaveAndGenerateSlug : handleGenerate}
                    disabled={generating || updatingSource || hasInvalidConversionId || (!useShortUrl && (!stagePipelineId || !leadSourceId)) || (useShortUrl && !slug)}
                >
                    {generating || updatingSource ? <Spinner animation="border" size="sm" /> : (useShortUrl ? 'Save & Generate URL' : 'Generate Token')}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
